/**
 * Upload seguro de anexos privados dos andamentos de Ordem de Serviço.
 *
 * RELEASE: 2026-10-09 v1.1.0 - Expõe URL temporária para miniaturas, preview e download interno.
 * RELEASE: 2026-10-09 v1.0.0 - Upload para bucket privado e registro validado por RPC.
 */
import { supabase } from '@/integrations/supabase/client';
import { validateFile } from '@/hooks/useTaskAttachments';
import { sanitizeStorageFileName } from '@/lib/storageFileName';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ServiceOrderAttachments');
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);

/** Classifica o arquivo sem confiar apenas no MIME enviado pelo navegador. */
function fileType(file: File): string {
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'archive';
  return 'document';
}

/** Envia o arquivo e remove o objeto automaticamente se o registro falhar. */
export async function uploadServiceOrderAttachment(params: {
  orderId: string;
  commentId: string;
  tenantId: string;
  file: File;
}): Promise<void> {
  const validation = validateFile(params.file);
  if (!validation.valid) throw new Error(validation.error);
  const safeName = sanitizeStorageFileName(params.file.name);
  const path = `${params.tenantId}/service-orders/${params.orderId}/${crypto.randomUUID()}_${safeName}`;
  const { error: uploadError } = await supabase.storage.from('service-orders').upload(path, params.file, {
    cacheControl: '3600', upsert: false, contentType: params.file.type || 'application/octet-stream',
  });
  if (uploadError) throw new Error('Falha ao enviar o arquivo');
  const { error } = await rpc('register_service_order_attachment', {
    p_id: params.orderId,
    p_comment_id: params.commentId,
    p_file_name: params.file.name,
    p_file_type: fileType(params.file),
    p_mime_type: params.file.type || 'application/octet-stream',
    p_file_size: params.file.size,
    p_storage_path: path,
    p_tenant_hint: params.tenantId,
  });
  if (!error) return;
  await supabase.storage.from('service-orders').remove([path]);
  logger.error('Erro ao registrar anexo da OS', { error: error.message });
  throw error;
}

/** Gera uma URL temporária do bucket privado para miniatura, preview ou download. */
export async function getServiceOrderAttachmentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('service-orders').createSignedUrl(path, 600);
  if (error || !data?.signedUrl) throw new Error('Não foi possível preparar o arquivo');
  return data.signedUrl;
}

/** Abre uma URL temporária do bucket privado, sem tornar o arquivo público. */
export async function openServiceOrderAttachment(path: string): Promise<void> {
  const signedUrl = await getServiceOrderAttachmentUrl(path);
  window.open(signedUrl, '_blank', 'noopener,noreferrer');
}